// =============================================================================
// VandieZ agent-web -- API client
// =============================================================================
// Thin fetch wrapper that:
//   * Resolves the base URL from VITE_API_BASE_URL (Vite injects at build).
//   * Serializes / parses JSON.
//   * Translates non-2xx responses into `ApiError` carrying the
//     RFC 7807 problem-detail body.
//   * Accepts an optional access token for authenticated calls.
//
// No global auth header injection -- callers pass the token explicitly so
// SSR / server-component callers (none today, but possible later) can use
// the same module without depending on a React context for state.
// =============================================================================

import {
  ApiError,
  type OtpRequestPayload,
  type OtpRequestResponse,
  type OtpVerifyPayload,
  type OtpVerifyResponse,
  type ProblemDetail,
  type RefreshResponse,
  type UserRead,
} from "@/types/auth";
import type {
  AgentInvitationPreview,
  AgentMyOwnersResponse,
  AgentAcceptRequest,
  AgentAcceptResponse,
} from "@/types/agent";
import type {
  Load,
  LoadCreatePayload,
  LoadListResponse,
  PublicPricingModel,
  ReferenceItem,
} from "@/types/load";
import type { Bid, BidListResponse, BidPlacePayload } from "@/types/bid";
import type {
  Trip,
  GpsHistoryResponse,
  SettlementStatement,
} from "@/types/trip";
import type { Vehicle, VehicleListResponse, VehiclePatchPayload } from "@/types/vehicle";
import type {
  AgentProfile,
  AgentProfileUpdate,
  TransactionList,
  WalletBalance,
} from "@/types/account";
import type {
  BrokerDashboard,
  BrokerLoadRow,
  BrokerLoadsResponse,
  BrokerMarginsResponse,
  BrokerRosterResponse,
  NearbyVehiclesResponse,
  BrokerReportResponse,
  AgentAnalysisResponse,
} from "@/types/broker";

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL) ??
  "http://localhost:8000/api/v1";

interface FetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  accessToken?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, accessToken, signal } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials: "omit",
  };
  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    fetchOptions.signal = signal;
  }

  const response = await fetch(`${BASE_URL}${path}`, fetchOptions);

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = {
        status: response.status,
        title: response.statusText,
        detail: text.slice(0, 500),
      };
    }
  }

  if (!response.ok) {
    const problem: ProblemDetail =
      parsed !== null &&
      typeof parsed === "object" &&
      "status" in (parsed)
        ? (parsed as ProblemDetail)
        : {
            status: response.status,
            title: response.statusText,
            detail: `Request failed with status ${response.status}`,
          };
    throw new ApiError(problem);
  }

  return parsed as T;
}

// =============================================================================
// Auth endpoint wrappers
// =============================================================================
export const authApi = {
  requestOtp(payload: OtpRequestPayload, signal?: AbortSignal): Promise<OtpRequestResponse> {
    return request<OtpRequestResponse>("/auth/otp/request", {
      method: "POST",
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  verifyOtp(payload: OtpVerifyPayload, signal?: AbortSignal): Promise<OtpVerifyResponse> {
    return request<OtpVerifyResponse>("/auth/otp/verify", {
      method: "POST",
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  refresh(refreshToken: string, signal?: AbortSignal): Promise<RefreshResponse> {
    return request<RefreshResponse>("/auth/refresh", {
      method: "POST",
      body: { refresh_token: refreshToken },
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  async logout(accessToken: string, refreshToken?: string): Promise<void> {
    await request<undefined>("/auth/logout", {
      method: "POST",
      accessToken,
      body: refreshToken ? { refresh_token: refreshToken } : undefined,
    });
  },

  me(accessToken: string, signal?: AbortSignal): Promise<UserRead> {
    return request<UserRead>("/auth/me", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// Agent + owner-scoped API modules
// =============================================================================

export const agentsApi = {
  preview(token: string, signal?: AbortSignal): Promise<AgentInvitationPreview> {
    return request<AgentInvitationPreview>(
      `/agents/invitations/preview?token=${encodeURIComponent(token)}`,
      { ...(signal !== undefined ? { signal } : {}) },
    );
  },

  accept(payload: AgentAcceptRequest): Promise<AgentAcceptResponse> {
    return request<AgentAcceptResponse>("/agents/accept", {
      method: "POST",
      body: payload,
    });
  },

  myOwners(
    accessToken: string,
    includeRevoked = false,
    signal?: AbortSignal,
  ): Promise<AgentMyOwnersResponse> {
    const qs = includeRevoked ? "?include_revoked=true" : "";
    return request<AgentMyOwnersResponse>(`/agents/me/owners${qs}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};


function buildOwnerScopedQuery(
  asOwnerId: string,
  extra: Record<string, string | number | undefined> = {},
): string {
  const qs = new URLSearchParams();
  qs.set("as", asOwnerId);
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined) {qs.set(k, String(v));}
  }
  return qs.toString().length > 0 ? `?${qs.toString()}` : "";
}


export const vehiclesApi = {
  list(
    accessToken: string,
    asOwnerId: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<VehicleListResponse> {
    const suffix = buildOwnerScopedQuery(asOwnerId, params);
    return request<VehicleListResponse>(`/vehicles${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(
    accessToken: string,
    vehicleId: string,
    asOwnerId: string,
    signal?: AbortSignal,
  ): Promise<Vehicle> {
    return request<Vehicle>(`/vehicles/${vehicleId}?as=${asOwnerId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  patch(
    accessToken: string,
    vehicleId: string,
    asOwnerId: string,
    payload: VehiclePatchPayload,
  ): Promise<Vehicle> {
    return request<Vehicle>(`/vehicles/${vehicleId}?as=${asOwnerId}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  toggleOnline(
    accessToken: string,
    vehicleId: string,
    asOwnerId: string,
  ): Promise<Vehicle> {
    return request<Vehicle>(
      `/vehicles/${vehicleId}/toggle-online?as=${asOwnerId}`,
      { method: "POST", accessToken },
    );
  },
};


/**
 * The pricing models a load may currently be posted under (p9).
 *
 * Enabled-only, straight from the catalog -- so enabling a model in the
 * admin screen makes it appear here with no client release, which is the
 * whole point of keeping the catalog in a table (D-2).
 */
/** Business master catalogs (C-24). Enabled-only, authed, any role. */
export const referenceDataApi = {
  listGoodsTypes(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<ReferenceItem[]> {
    return request<ReferenceItem[]>("/reference-data?category=goods_type", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

export const pricingModelsApi = {
  listEnabled(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<PublicPricingModel[]> {
    return request<PublicPricingModel[]>("/platform/pricing-models", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

export const loadsApi = {
  list(
    accessToken: string,
    asOwnerId: string,
    params: { limit?: number; offset?: number; status?: string } = {},
    signal?: AbortSignal,
  ): Promise<LoadListResponse> {
    const suffix = buildOwnerScopedQuery(asOwnerId, params);
    return request<LoadListResponse>(`/loads${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(
    accessToken: string,
    loadId: string,
    asOwnerId: string,
    signal?: AbortSignal,
  ): Promise<Load> {
    return request<Load>(`/loads/${loadId}?as=${asOwnerId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  /**
   * Post a load the AGENT owns (AG-12, FSD p66: "Agent can post many
   * loads per day unlike a normal customer").
   *
   * No `?as=` — this is not delegated work. The agent is the shipper on
   * loads they post, which is why these do not need an owner context
   * the way `list`/`detail` above do.
   */
  create(
    accessToken: string,
    payload: LoadCreatePayload,
  ): Promise<Load> {
    return request<Load>("/loads", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  /**
   * The agent's OWN loads — "Agent My Loads" (p65).
   *
   * `mine=true` self-scopes; without it the server returns the
   * marketplace, which is `open_for_bidding` only, so a load would
   * vanish from this list the moment it was accepted.
   */
  listMine(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<LoadListResponse> {
    const q = new URLSearchParams({ mine: "true" });
    if (params.limit !== undefined) {q.set("limit", String(params.limit));}
    if (params.offset !== undefined) {q.set("offset", String(params.offset));}
    return request<LoadListResponse>(`/loads?${q.toString()}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};


export const bidsApi = {
  myBids(
    accessToken: string,
    asOwnerId: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<BidListResponse> {
    const suffix = buildOwnerScopedQuery(asOwnerId, params);
    return request<BidListResponse>(`/bids${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  place(
    accessToken: string,
    loadId: string,
    asOwnerId: string,
    payload: BidPlacePayload,
  ): Promise<Bid> {
    return request<Bid>(`/loads/${loadId}/bids?as=${asOwnerId}`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};


export const tripsApi = {
  list(
    accessToken: string,
    asOwnerId: string,
    params: { limit?: number; offset?: number; status?: string } = {},
    signal?: AbortSignal,
  ) {
    const suffix = buildOwnerScopedQuery(asOwnerId, params);
    return request<{ items: Trip[]; total: number; limit: number; offset: number }>(
      `/trips${suffix}`,
      {
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    tripId: string,
    asOwnerId: string,
    signal?: AbortSignal,
  ): Promise<Trip> {
    return request<Trip>(`/trips/${tripId}?as=${asOwnerId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  // C-21. NOT owner-scoped: the settlement endpoint decides what the
  // caller may see from the caller's own identity, and passing `as=` would
  // imply this page can ask for somebody else's lines. It cannot -- the
  // backend returns only the authenticated user's rows.
  settlement(
    accessToken: string,
    tripId: string,
    signal?: AbortSignal,
  ): Promise<SettlementStatement> {
    return request<SettlementStatement>(`/trips/${tripId}/settlement`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  gpsHistory(
    accessToken: string,
    tripId: string,
    asOwnerId: string,
    signal?: AbortSignal,
  ): Promise<GpsHistoryResponse> {
    return request<GpsHistoryResponse>(
      `/gps/${tripId}/gps?as=${asOwnerId}`,
      { accessToken, ...(signal !== undefined ? { signal } : {}) },
    );
  },
};

// =============================================================================
// WS-A broker module
// =============================================================================
// Every endpoint is scoped to the calling agent server-side. Brokers on this
// platform are direct competitors, so nothing here takes a broker id -- the
// token IS the scope, and there is no parameter an oversight could widen.
export const brokerApi = {
  dashboard(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<BrokerDashboard> {
    return request<BrokerDashboard>("/broker/dashboard", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  loads(
    accessToken: string,
    opts: { status?: string } = {},
    signal?: AbortSignal,
  ): Promise<BrokerLoadsResponse> {
    const qs = opts.status ? `?status=${encodeURIComponent(opts.status)}` : "";
    return request<BrokerLoadsResponse>(`/broker/loads${qs}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  margins(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<BrokerMarginsResponse> {
    return request<BrokerMarginsResponse>("/broker/margins", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  roster(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<BrokerRosterResponse> {
    return request<BrokerRosterResponse>("/broker/drivers", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  /**
   * AG-09 performance report for a date window.
   *
   * The server picks the comparison window itself — same length as the
   * one requested, ending where it begins — so there is nothing here to
   * get wrong by passing a mismatched pair.
   */
  reports(
    accessToken: string,
    opts: { fromDate: string; toDate: string },
    signal?: AbortSignal,
  ): Promise<BrokerReportResponse> {
    const qs =
      `?from_date=${encodeURIComponent(opts.fromDate)}` +
      `&to_date=${encodeURIComponent(opts.toDate)}`;
    return request<BrokerReportResponse>(`/broker/reports${qs}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  /** p69: route / driver / goods-type / cancellation analysis + status
   *  counts. `from` and `to` are REQUIRED by the server. */
  analysis(
    accessToken: string,
    params: { from: string; to: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<AgentAnalysisResponse> {
    const qs = new URLSearchParams({
      from_date: params.from,
      to_date: params.to,
    });
    if (params.limit !== undefined) {
      qs.set("limit", String(params.limit));
    }
    return request<AgentAnalysisResponse>(
      `/broker/reports/analysis?${qs.toString()}`,
      { accessToken, ...(signal !== undefined ? { signal } : {}) },
    );
  },

  nearbyVehicles(
    accessToken: string,
    params: { lat: number; lng: number; radiusKm?: number },
    signal?: AbortSignal,
  ): Promise<NearbyVehiclesResponse> {
    const qs = new URLSearchParams({
      lat: String(params.lat),
      lng: String(params.lng),
    });
    if (params.radiusKm !== undefined) {
      qs.set("radius_km", String(params.radiusKm));
    }
    return request<NearbyVehiclesResponse>(
      `/broker/nearby-vehicles?${qs.toString()}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  takeLoad(
    accessToken: string,
    loadId: string,
    customerPriceInr: string,
    notes?: string,
  ): Promise<BrokerLoadRow> {
    return request<BrokerLoadRow>(`/broker/loads/${loadId}/take`, {
      method: "POST",
      accessToken,
      body: {
        customer_price_inr: customerPriceInr,
        ...(notes ? { notes } : {}),
      },
    });
  },
};

// =============================================================================
// Agent account -- profile (FSD p58) and payments (FSD p61)
// =============================================================================
// Self-scoped, so NO `?as=<ownerId>`: an agent's own company details and
// their own wallet are theirs, not the owner's they happen to be acting
// for. Sending the owner scope here would show a delegate somebody else's
// money.
export const accountApi = {
  profile(accessToken: string, signal?: AbortSignal): Promise<AgentProfile> {
    return request<AgentProfile>("/users/me", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  updateProfile(
    accessToken: string,
    payload: AgentProfileUpdate,
  ): Promise<AgentProfile> {
    return request<AgentProfile>("/users/me", {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  wallet(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<WalletBalance> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {
      qs.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      qs.set("offset", String(params.offset));
    }
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<WalletBalance>(`/users/me/wallet${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  /** Per-trip rows: gross, commission and where it went. */
  transactions(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<TransactionList> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {
      qs.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      qs.set("offset", String(params.offset));
    }
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<TransactionList>(`/users/me/transactions${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};
